/**
 * Compliance Reports Page
 *
 * Regulation U compliance monitoring and reporting.
 * Requires compliance:read permission.
 *
 * All data fetched from backend via Prisma-backed endpoints.
 * Renders empty states when no data exists and says a read failed when one
 * did -- no fake data fallback, and no absent value rendered as a measured
 * zero (.claude/rules/no-fabricated-values.md).
 */

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock as ScheduleIcon,
  History as HistoryIcon,
  ShieldCheck as ShieldCheckIcon,
  FileBarChart as ReportIcon,
} from 'lucide-react';
import {
  Alert,
  BodyText,
  DataTable,
  Card,
  Button,
  Badge,
  EmptyState,
  Tabs,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Select,
  Skeleton,
  SkeletonTable,
  formatCalendarDate,
  formatUsd,
} from '@aaimplatform/ui';
import type { BadgeProps } from '@aaimplatform/ui';

import { useAuthStore } from '../stores/auth';
import { apiClient } from '../api/client';
import { useQuery } from '@tanstack/react-query';
import { platformAdminClient } from '../api/client';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import type { AdminLoanCompliance } from '@aaimplatform/institutional-types';
import { hasPlatformAdminRole } from '../lib/platform-admin-roles';

type ComplianceSummary = AdminLoanCompliance.components['schemas']['ComplianceSummary'];
type ComplianceAlert = AdminLoanCompliance.components['schemas']['ComplianceAlert'];
type AlertType = AdminLoanCompliance.components['schemas']['AlertType'];
type AlertSeverity = AdminLoanCompliance.components['schemas']['AlertSeverity'];
type AlertStatus = AdminLoanCompliance.components['schemas']['AlertStatus'];
type RegUReport = components['schemas']['RegUReport'];

/** AlertType display labels. Every enum value has a sentence-case label. */
const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  LTV_BREACH: 'LTV breach',
  COLLATERAL_WARNING: 'Collateral warning',
  COLLATERAL_CONCENTRATION: 'Collateral concentration',
  COLLATERAL_HEALTH_ALERT: 'Collateral Health Alert',
  REGULATION_U_VIOLATION: 'Regulation U violation',
  FINRA_4330_VIOLATION: 'FINRA 4330 violation',
  SEC_REPORTING_REQUIRED: 'SEC reporting required',
  STATE_COMPLIANCE_ISSUE: 'State compliance issue',
};

const ALERT_SEVERITY: Record<AlertSeverity, { label: string; variant: BadgeProps['variant'] }> = {
  HIGH: { label: 'High', variant: 'error' },
  MEDIUM: { label: 'Medium', variant: 'warning' },
  LOW: { label: 'Low', variant: 'neutral' },
};

const ALERT_STATUS: Record<AlertStatus, { label: string; variant: BadgeProps['variant'] }> = {
  OPEN: { label: 'Open', variant: 'error' },
  ACKNOWLEDGED: { label: 'Acknowledged', variant: 'warning' },
  RESOLVED: { label: 'Resolved', variant: 'success' },
};

function useComplianceSummary() {
  return useQuery<ComplianceSummary>({
    queryKey: ['admin', 'compliance', 'summary'],
    queryFn: async () => apiClient.get<ComplianceSummary>('/admin/compliance/summary'),
  });
}

function useComplianceAlerts() {
  return useQuery<{ alerts: ComplianceAlert[] }>({
    queryKey: ['admin', 'compliance', 'alerts'],
    queryFn: async () => apiClient.get<{ alerts: ComplianceAlert[] }>('/admin/compliance/alerts'),
  });
}

function useComplianceReports() {
  return useQuery({
    queryKey: ['admin', 'compliance', 'reports'],
    queryFn: async (): Promise<{ reports: RegUReport[] }> => {
      const { data, error } = await platformAdminClient.GET('/compliance/reports');
      if (error) throw new Error('Failed to fetch compliance reports');
      return { reports: data.reports ?? [] };
    },
  });
}

/**
 * A Reg U report field the source does not measure yet is omitted from the
 * response; it renders as words, never as a measured $0 or 0%.
 */
function measured(value: unknown, format: (n: number) => string): ReactNode {
  if (typeof value === 'number' && Number.isFinite(value)) return format(value);
  return (
    <span className="font-sans text-tertiary" title="The report source does not measure this value">
      Not measured
    </span>
  );
}

/**
 * The Regulation U Reports Period choices. getComplianceReports declares no
 * period parameter, so the select filters the returned rows by their period
 * label (RegUReport.period, a quarter label such as 'Q3 2026'). r3 fi-g6-6
 * removed the select because nothing read it; the operator ruled it returns
 * as this row filter (ui-followon-unit).
 */
const REPORT_PERIOD_OPTIONS = [
  { value: 'current', label: 'Current Quarter' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'all', label: 'All Periods' },
];

const QUARTER_LABEL = /^Q([1-4])\s+(\d{4})$/;

/**
 * Reg U report rows for a Period choice, judged by each row's quarter label
 * against `now` in the viewer's calendar. 'current' keeps the quarter `now`
 * falls in; 'ytd' keeps this year's quarters up to and including it; any
 * other choice keeps every row. A row whose label is not a quarter label
 * cannot be placed in a quarter, so it appears under All Periods only.
 */
export function filterReportsByPeriod(
  reports: RegUReport[],
  period: string,
  now: Date = new Date(),
): RegUReport[] {
  if (period !== 'current' && period !== 'ytd') return reports;
  const year = now.getFullYear();
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  return reports.filter((report) => {
    const match = QUARTER_LABEL.exec(report.period?.trim() ?? '');
    if (!match) return false;
    const reportQuarter = Number(match[1]);
    if (Number(match[2]) !== year) return false;
    return period === 'current' ? reportQuarter === quarter : reportQuarter <= quarter;
  });
}

/** A compliance tile's caption when no loan carries a compliance record. */
const NO_RECORDS_CAPTION = 'No records';

/** A summary count as text, or the withheld marker when the field is absent. */
function countText(value: unknown): { value: string; caption?: string } {
  return typeof value === 'number' && Number.isFinite(value)
    ? { value: String(value) }
    : { value: '--', caption: METRIC_READ_CAPTIONS.notReported };
}

export function ComplianceReportsPage() {
  const { user } = useAuthStore();

  // GH #1963. Every permission check on this page was case-SENSITIVE
  // (`permissions.includes('compliance:read')`), which
  // .claude/rules/authz-case-insensitive.md forbids: the JWT's permission
  // strings and the literal must be lowercased on BOTH sides, or half the
  // platform silently 403s. Platform-admin roles also carry these
  // permissions implicitly -- the same posture the nav uses to gate
  // /audit/pledges -- so a superadmin is never locked out of a surface they
  // administer.
  const lc = (v: unknown): string => (typeof v === 'string' ? v.toLowerCase() : '');
  const isPlatformAdmin = hasPlatformAdminRole(user?.roles);
  const hasPermission = (target: string): boolean =>
    isPlatformAdmin || (user?.permissions ?? []).some((p) => lc(p) === target);
  const [activeTab, setActiveTab] = useState(0);
  const [reportPeriod, setReportPeriod] = useState('current');
  const navigate = useNavigate();

  const { data: summary, isLoading: summaryLoading, error: summaryError } = useComplianceSummary();
  const { data: alertsData, isLoading: alertsLoading, error: alertsError } = useComplianceAlerts();
  const { data: reportsData, isLoading: reportsLoading, error: reportsError } = useComplianceReports();

  const alerts = alertsData?.alerts ?? [];
  const reports = reportsData?.reports ?? [];
  const periodReports = filterReportsByPeriod(reports, reportPeriod);

  const canViewCompliance = hasPermission('compliance:read');
  const canReadAudit = hasPermission('audit:read');

  if (!canViewCompliance) {
    return (
      <div className="max-w-3xl mx-auto mt-8">
        <Alert type="error">You do not have permission to view compliance reports.</Alert>
      </div>
    );
  }

  if (summaryError) {
    return (
      <Alert type="error" title="Compliance data unavailable">
        Failed to load compliance data. Please try again.
      </Alert>
    );
  }

  // Both audit dates are optional in the summary contract (ruling
  // absence-representable-contracts): the service omits lastAuditDate when the
  // tenant has no compliance record and nextAuditDate when no check is
  // scheduled. An absent date prints no date line.
  const lastAuditDate = summary?.lastAuditDate;
  const nextAuditDate = summary?.nextAuditDate;

  const totalLoans = summary ? countText(summary.totalLoans) : null;
  const compliantLoans = summary ? countText(summary.compliantLoans) : null;
  const warningLoans = summary ? countText(summary.warningLoans) : null;
  const violationLoans = summary ? countText(summary.violationLoans) : null;

  // The Dashboard's rule (admin-portal.routes.ts, the FI dashboard summary):
  // warning and violation counts measure only loans carrying a compliance
  // record, so with none all three compliance tiles say 'No records' rather
  // than reading a zero as a check that ran (r3 fi-cons-7).
  const noComplianceRecords = !!summary && summary.totalLoans === 0;

  return (
    <div>
      {/* Page Header. The Export Report button was removed on 2026-09-27: it
          rendered permanently disabled because no compliance export endpoint
          exists in any spec, and a control that can never act is a broken UI
          (ui-package.md, Save Button Discipline). It returns with a CSV
          endpoint authored spec-first. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">Regulation U compliance monitoring and reporting</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryLoading ? (
        <MetricCardGrid className="mb-6">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </MetricCardGrid>
      ) : summary && totalLoans && compliantLoans && warningLoans && violationLoans ? (
        <MetricCardGrid className="mb-6">
          {/* The population is loans carrying a compliance record, the term
              the empty state below uses; 'Total Loans' read as the lending
              book, which the Dashboard counts as Active Loans (r3 fi-cons-7). */}
          <MetricCard label="Monitored Loans" value={totalLoans.value} caption={totalLoans.caption} />
          <MetricCard
            label="Compliant"
            value={compliantLoans.value}
            caption={compliantLoans.caption}
            description={noComplianceRecords ? NO_RECORDS_CAPTION : undefined}
            change={summary.compliantLoans > 0 ? 'In compliance' : undefined}
            changeType="positive"
          />
          <MetricCard
            label="Warnings"
            value={warningLoans.value}
            caption={warningLoans.caption}
            description={noComplianceRecords ? NO_RECORDS_CAPTION : undefined}
            change={summary.warningLoans > 0 ? 'Action needed' : undefined}
            changeType="neutral"
          />
          {/* A zero violation count carries no caption, like the Compliant
              and Warnings tiles: a green 'None' read as an all-clear even when
              no loan is monitored (r3 fi-g6-7). */}
          <MetricCard
            label="Violations"
            value={violationLoans.value}
            caption={violationLoans.caption}
            description={noComplianceRecords ? NO_RECORDS_CAPTION : undefined}
            change={
              typeof summary.violationLoans === 'number' && summary.violationLoans > 0
                ? 'Critical'
                : undefined
            }
            changeType="negative"
          />
        </MetricCardGrid>
      ) : null}

      {/* Audit Schedule */}
      {summary && (
        <Card className="mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <ScheduleIcon className="text-tertiary" />
            <div className="flex-1">
              <BodyText variant="ui" className="text-sm">
                {lastAuditDate
                  ? `Last audit: ${formatCalendarDate(lastAuditDate)}`
                  : 'No compliance audit on record'}
              </BodyText>
            </div>
            {nextAuditDate && (
              <Badge variant="neutral">Next audit: {formatCalendarDate(nextAuditDate)}</Badge>
            )}
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'alerts', label: `Active Alerts (${alerts.filter(a => a.status === 'OPEN').length})` },
          { id: 'reports', label: 'Regulation U Reports' },
          { id: 'audit', label: 'Audit Log' },
        ]}
        activeTab={activeTab === 0 ? 'alerts' : activeTab === 1 ? 'reports' : 'audit'}
        onTabChange={(id: string) => setActiveTab(id === 'alerts' ? 0 : id === 'reports' ? 1 : 2)}
        className="mb-4"
      />

      {/* Alerts Tab */}
      {activeTab === 0 && (
        alertsLoading ? (
          <SkeletonTable rows={3} columns={7} />
        ) : alertsError ? (
          <Alert type="error" title="Alerts unavailable">
            Compliance alerts could not be loaded, so none are shown. Please try again.
          </Alert>
        ) : alerts.length > 0 ? (
          <DataTable<ComplianceAlert>
            columns={[
              {
                header: 'Severity',
                accessor: 'severity',
                cell: (value) => {
                  const severity = ALERT_SEVERITY[value as AlertSeverity];
                  return severity ? (
                    <Badge variant={severity.variant} size="sm">{severity.label}</Badge>
                  ) : (
                    <Badge variant="neutral" size="sm">Not reported</Badge>
                  );
                },
              },
              {
                header: 'Type',
                accessor: (row) => ALERT_TYPE_LABELS[row.type] ?? 'Other',
              },
              { header: 'Loan', accessor: 'loanNumber' },
              { header: 'Member', accessor: 'memberName' },
              { header: 'Message', accessor: 'message', cell: (value) => <span className="max-w-xs truncate block">{String(value)}</span> },
              { header: 'Date', accessor: (row) => formatCalendarDate(row.createdAt) },
              {
                header: 'Status',
                accessor: 'status',
                cell: (value) => {
                  const status = ALERT_STATUS[value as AlertStatus];
                  return status ? (
                    <Badge variant={status.variant} size="sm">{status.label}</Badge>
                  ) : (
                    <Badge variant="neutral" size="sm">Not reported</Badge>
                  );
                },
              },
            ]}
            data={alerts}
            rowKey="id"
            striped
            hoverable
          />
        ) : (
          <Card>
            <EmptyState
              icon={<ShieldCheckIcon className="h-8 w-8" />}
              title="No active compliance alerts"
              description={
                summary?.totalLoans === 0
                  ? 'Alerts appear here when a compliance check flags a monitored loan. No monitored loans are recorded for your institution.'
                  : 'Alerts appear here when a compliance check flags a monitored loan.'
              }
            />
          </Card>
        )
      )}

      {/* Reports Tab */}
      {activeTab === 1 && (
        <div>
          {/* The Period select filters the rows the read returned by their
              quarter label (filterReportsByPeriod). It shows only once there
              are rows to filter. */}
          {!reportsLoading && !reportsError && reports.length > 0 && (
            <div className="mb-4">
              <Select
                label="Period"
                options={REPORT_PERIOD_OPTIONS}
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
                fullWidth={false}
              />
            </div>
          )}
          {reportsLoading ? (
            <SkeletonTable rows={3} columns={8} />
          ) : reportsError ? (
            <Alert type="error" title="Reports unavailable">
              Regulation U reports could not be loaded, so none are shown. Please try again.
            </Alert>
          ) : periodReports.length > 0 ? (
            <DataTable<RegUReport>
              columns={[
                { header: 'Period', accessor: 'period' },
                { header: 'Total Exposure', accessor: 'totalExposure', align: 'right', monospace: true, cell: (v) => measured(v, formatUsd) },
                { header: 'Margin Loans', accessor: 'marginLoans', align: 'right', monospace: true, cell: (v) => measured(v, String) },
                { header: 'Purpose', accessor: 'purposeLoans', align: 'right', monospace: true, cell: (v) => measured(v, String) },
                { header: 'Non-Purpose', accessor: 'nonPurposeLoans', align: 'right', monospace: true, cell: (v) => measured(v, String) },
                { header: 'Avg LTV', accessor: 'avgLTV', align: 'right', monospace: true, cell: (v) => measured(v, (n) => `${n}%`) },
                { header: 'Max LTV', accessor: 'maxLTV', align: 'right', monospace: true, cell: (v) => measured(v, (n) => `${n}%`) },
                {
                  header: 'Violations',
                  accessor: 'violations',
                  align: 'right',
                  cell: (value) =>
                    typeof value === 'number' ? (
                      <Badge variant={value > 0 ? 'error' : 'success'} size="sm">{String(value)}</Badge>
                    ) : (
                      measured(value, String)
                    ),
                },
              ]}
              data={periodReports}
              rowKey="period"
              striped
              hoverable
            />
          ) : reports.length > 0 ? (
            <Card>
              <EmptyState
                icon={<ReportIcon className="h-8 w-8" />}
                title="No Regulation U report for this period"
                description="Reports exist for other quarters. Choose All Periods to see every quarter on record."
              />
            </Card>
          ) : (
            <Card>
              <EmptyState
                icon={<ReportIcon className="h-8 w-8" />}
                title="No Regulation U reports yet"
                description="A report appears for each quarter in which a Regulation U compliance check ran on a monitored loan."
              />
            </Card>
          )}
        </div>
      )}

      {/* Audit Log Tab (GH #1963).
          Was a never-finished stub that rendered 'Audit log requires
          audit:read permission...' UNCONDITIONALLY -- no permission check at
          all, so even a platform superadmin read it as a permission failure.
          The real audit surface exists at /audit/pledges (PledgeAuditTrail,
          gated on audit:read in the nav). This tab now tells the truth: it
          checks the caller's ACTUAL permission and either routes them to that
          surface or says plainly that they lack it. Comparison is
          case-insensitive on both sides per authz-case-insensitive.md. */}
      {activeTab === 2 && (
        <Card>
          <div className="p-12 text-center">
            {canReadAudit ? (
              <>
                <BodyText variant="ui" className="text-secondary mb-6">
                  The pledge audit trail is the system of record for compliance
                  events: every pledge state transition, its actor and its timestamp.
                </BodyText>
                {/* One control, not a button nested in a link (r3 fi-g6-9). */}
                <Button
                  variant="primary"
                  icon={HistoryIcon}
                  iconPosition="left"
                  onClick={() => navigate('/audit/pledges')}
                >
                  Open Pledge Audit Trail
                </Button>
              </>
            ) : (
              <BodyText variant="ui" className="text-secondary">
                You do not have the audit:read permission required to view the
                audit trail. Ask an administrator at your institution to grant it.
              </BodyText>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
